import { DictTypes } from '@/constants';
import { cachedApi, cachedDictApi } from '@/services/helpers/api';
import { cacheDict, getCachedDict } from '@/services/helpers/cacheData';
import { type CacheConfig } from '@/types/api';
import { type GetDictContentType } from '@/types/dicts';
import {
  type TAddProductDictionaryResponse,
  type TProductInsuranceServerResponse
} from '@/types/store';

import { ApiConnector } from '../api.connector';

type CityType = 'all' | 'dealers';
type CityTypeDictMap = {
  [K in CityType]: K extends 'all'
    ? DictTypes.Cities
    : K extends 'dealers'
      ? DictTypes.DealersCities
      : never;
};
type CityTypeContentMap = {
  [K in CityType]: GetDictContentType<CityTypeDictMap[K]>[];
};

class DictionariesApi extends ApiConnector {
  async getOptimizedDictionaries(
    dictsToFind: DictTypes[],
    updatingDicts: DictTypes[],
    cacheConfig: CacheConfig
  ): Promise<{ name: DictTypes; content: GetDictContentType<DictTypes>[] }[]> {
    const path = `dictionaries/getBulk`;

    const result: {
      name: DictTypes;
      content: GetDictContentType<DictTypes>[];
    }[] = [];
    const dictsToRequest: DictTypes[] = [];

    dictsToFind.forEach((dict) => {
      if (
        !cacheConfig.ignoreCache ||
        (updatingDicts.length > 0 && !updatingDicts.includes(dict))
      ) {
        const cachedData = getCachedDict(dict);
        if (cachedData) {
          result.push({ name: dict, content: cachedData });
        } else {
          dictsToRequest.push(dict);
        }
      } else {
        dictsToRequest.push(dict);
      }
    });

    if (dictsToRequest.length > 0) {
      const response = (await this.call({
        path,
        method: 'post',
        body: {
          dicts: dictsToRequest,
          ...(updatingDicts.length > 0 ? { updating: updatingDicts } : {})
        }
      })) as { name: DictTypes; content: GetDictContentType<DictTypes>[] }[];
      response.forEach((responseItem) => {
        cacheDict(responseItem.name, responseItem.content);
        result.push(responseItem);
      });
    }

    return result;
  }

  // Запрос на последние даты обновление словарей
  getDictionariesDates(): Promise<Array<{ name: string; date: string }>> {
    return this.call({ path: `dictionaries/dicts/all/controlDate` });
  }

  getProductsDictionary(
    cacheConfig: CacheConfig
  ): Promise<TAddProductDictionaryResponse[]> {
    const path = 'requests/products/getProductsDict';
    return cachedApi({
      path,
      call: () => this.call({ path }),
      cacheConfig: { ...cacheConfig, shortTerm: true }
    });
  }

  getProductInsurance(
    cacheConfig: CacheConfig
  ): Promise<TProductInsuranceServerResponse[]> {
    const path = 'requests/products/getProductInsurance';
    return cachedApi({
      path,
      call: () => this.call({ path }),
      cacheConfig: { ...cacheConfig, shortTerm: false }
    });
  }

  getCitiesForFilter(
    type: 'all',
    cacheConfig: CacheConfig
  ): Promise<CityTypeContentMap['all']>;
  getCitiesForFilter(
    type: 'dealers',
    cacheConfig: CacheConfig
  ): Promise<CityTypeContentMap['dealers']>;
  getCitiesForFilter(
    type: CityType,
    cacheConfig: CacheConfig
  ): Promise<CityTypeContentMap[typeof type]> {
    const dictTypeMap: CityTypeDictMap = {
      all: DictTypes.Cities,
      dealers: DictTypes.DealersCities
    };

    return cachedDictApi({
      dictType: dictTypeMap[type],
      call: () => this.call({ path: `requests/cities/getForFilter/${type}` }),
      cacheConfig: {
        ignoreCache: cacheConfig.ignoreCache
      }
    });
  }
}

export const dictionariesApi = new DictionariesApi();
